import { useEffect, useMemo, useRef } from 'react';

export interface SlashCommandDef {
  /** Command without the leading slash, e.g. `analyze`. */
  name: string;
  /** Short description shown in the menu. */
  description: string;
  /** Optional arg hint shown after the command name, e.g. `<test>`. */
  argHint?: string;
  /** Marks commands that aren't wired up yet so users see the
   *  namespace but get a friendly "coming soon" instead of a fail. */
  comingSoon?: boolean;
}

interface SlashCommandMenuProps {
  /** The current input string (must start with `/` for the menu to
   *  do anything useful — caller decides whether to render). */
  query: string;
  /** Index of the highlighted item — owned by the parent so arrow-
   *  key navigation and click selection share state. */
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  onSelect: (command: SlashCommandDef) => void;
  commands: SlashCommandDef[];
}

/**
 * Slash-command popover for the AI bubble's input field. Cursor /
 * Discord / Slack-style: typing `/` opens the menu, the input below
 * filters by prefix, Enter selects.
 *
 * The menu itself is dumb — selection handling, keyboard nav, and
 * filtering all live in the parent. That keeps the input event
 * pipeline (which already manages composition events, IME, voice
 * dictation) in one place.
 */
export const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({
  query,
  activeIndex,
  setActiveIndex,
  onSelect,
  commands,
}) => {
  const filtered = useMemo(() => filterCommands(commands, query), [commands, query]);
  const listRef = useRef<HTMLDivElement>(null);

  // Clamp activeIndex when the filtered set changes so the highlight
  // doesn't point at a removed row.
  useEffect(() => {
    if (filtered.length === 0) return;
    if (activeIndex >= filtered.length) setActiveIndex(0);
  }, [filtered.length, activeIndex, setActiveIndex]);

  // Scroll the highlighted row into view as the user arrows down a
  // long list (e.g. `/help` showing every coming-soon command).
  useEffect(() => {
    const el = listRef.current?.children?.[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (filtered.length === 0) return null;

  return (
    <div
      className="mx-3 mb-2 rounded-xl overflow-hidden"
      style={{
        // bg-tertiary always reads as the "raised" tier in this theme
        // system: in light it's slightly darker than the page, in dark
        // it's slightly lighter than the chat area's bg-secondary —
        // both produce a deliberate elevated-popup feel without the
        // "wrong direction" inversion that bg-primary causes in dark.
        backgroundColor: 'var(--color-bg-tertiary)',
        border: '1px solid var(--color-border-primary)',
        boxShadow:
          '0 8px 24px color-mix(in srgb, var(--color-text-primary) 14%, transparent)',
        maxHeight: '260px',
      }}
    >
      <div
        ref={listRef}
        className="overflow-y-auto py-1"
        style={{ maxHeight: '260px' }}
      >
        {filtered.map((cmd, i) => {
          const isActive = i === activeIndex;
          return (
            <div
              key={cmd.name}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => {
                // mousedown not click — the input doesn't lose focus
                // before the selection fires, and IME composition
                // doesn't get cancelled.
                e.preventDefault();
                onSelect(cmd);
              }}
              className="relative flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors"
              style={{
                backgroundColor: isActive
                  ? 'color-mix(in srgb, var(--color-primary-500) 14%, transparent)'
                  : 'transparent',
                opacity: cmd.comingSoon ? 0.6 : 1,
              }}
            >
              {isActive && (
                <span
                  aria-hidden
                  className="absolute top-1.5 bottom-1.5 left-0 w-0.5 rounded-r"
                  style={{ backgroundColor: 'var(--color-primary-600)' }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span
                    className="font-mono text-[13px] tabular-nums truncate"
                    style={{
                      color: isActive
                        ? 'var(--color-primary-700)'
                        : 'var(--color-primary-600)',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    /{cmd.name}
                  </span>
                  {cmd.argHint ? (
                    <span
                      className="font-mono text-[12px] truncate"
                      style={{
                        color: 'var(--color-text-tertiary)',
                        fontWeight: 400,
                      }}
                    >
                      {cmd.argHint}
                    </span>
                  ) : null}
                </div>
                <div
                  className="text-[12px] leading-snug truncate mt-0.5"
                  style={{
                    color: isActive
                      ? 'var(--color-text-secondary)'
                      : 'var(--color-text-tertiary)',
                  }}
                >
                  {cmd.description}
                </div>
              </div>
              {cmd.comingSoon && (
                <span
                  className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
                  style={{
                    backgroundColor:
                      'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
                    color: 'var(--color-text-tertiary)',
                    fontWeight: 600,
                  }}
                >
                  Soon
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div
        className="px-3 py-1.5 text-[10px] flex items-center justify-between border-t"
        style={{
          borderColor: 'var(--color-border-primary)',
          color: 'var(--color-text-tertiary)',
          backgroundColor:
            'color-mix(in srgb, var(--color-text-primary) 3%, transparent)',
        }}
      >
        <span>↑↓ navigate · ↵ select · esc dismiss</span>
        <span>{filtered.length} command{filtered.length === 1 ? '' : 's'}</span>
      </div>
    </div>
  );
};

/**
 * Prefix-filter the command set against the user's input. Empty
 * `query` (just `/`) returns the full active set; coming-soon
 * commands are pushed to the bottom so working ones surface first.
 */
const filterCommands = (
  commands: SlashCommandDef[],
  query: string,
): SlashCommandDef[] => {
  if (!query.startsWith('/')) return [];
  const stripped = query.slice(1).toLowerCase().split(/\s+/, 1)[0] ?? '';
  const matches = commands.filter((c) =>
    !stripped ? true : c.name.toLowerCase().startsWith(stripped),
  );
  // Stable sort: working first, coming-soon after, alphabetical
  // within each bucket.
  return matches.sort((a, b) => {
    if (Boolean(a.comingSoon) !== Boolean(b.comingSoon)) {
      return a.comingSoon ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });
};

export const matchSlashCommand = (
  commands: SlashCommandDef[],
  message: string,
): { command: SlashCommandDef; args: string } | null => {
  const trimmed = message.trim();
  if (!trimmed.startsWith('/')) return null;
  const space = trimmed.indexOf(' ');
  const name = (space === -1 ? trimmed.slice(1) : trimmed.slice(1, space)).toLowerCase();
  const args = space === -1 ? '' : trimmed.slice(space + 1).trim();
  const cmd = commands.find((c) => c.name.toLowerCase() === name);
  return cmd ? { command: cmd, args } : null;
};
